/**
 * IndexedDB wrapper for offline storage and cross-session persistence
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { FormState, OrderPayload } from './types';

interface SprintRayDB extends DBSchema {
  formState: {
    key: string;
    value: FormState;
  };
  pendingOrders: {
    key: string;
    value: OrderPayload;
  };
  completedOrders: {
    key: string;
    value: {
      order: OrderPayload;
      savedAt: string;
      expiresAt: string;
      orderId?: string;
    };
  };
}

const DB_NAME = 'SprintRayOrderForm';
const DB_VERSION = 2;
const FORM_STATE_KEY = 'currentFormState';
const PENDING_ORDERS_STORE = 'pendingOrders';
const COMPLETED_ORDERS_STORE = 'completedOrders';

// Completed Orders Configuration
const COMPLETED_ORDER_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const MAX_COMPLETED_ORDERS = 50;

class OfflineStorage {
  private db: IDBPDatabase<SprintRayDB> | null = null;
  private initPromise: Promise<void> | null = null;
  private isInitializing = false;

  /**
   * Initialize the database with proper error handling and connection validation
   */
  async init(): Promise<void> {
    // Return existing promise if initialization is in progress
    if (this.initPromise) {
      return this.initPromise;
    }

    // Return early if already initialized
    if (this.db) {
      return;
    }

    // Prevent concurrent initializations
    if (this.isInitializing) {
      // Wait for ongoing initialization
      await new Promise(resolve => setTimeout(resolve, 50));
      return this.init();
    }

    this.isInitializing = true;
    this.initPromise = this.performInit();

    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
      this.isInitializing = false;
    }
  }

  /**
   * Perform the actual database initialization
   */
  private async performInit(): Promise<void> {
    try {
      console.log('🔧 [IndexedDB] Initializing database...');

      this.db = await openDB<SprintRayDB>(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, transaction) {
          console.log(`📊 [IndexedDB] Upgrading database from version ${oldVersion} to ${newVersion}`);

          // Form state store
          if (!db.objectStoreNames.contains('formState')) {
            console.log('➕ [IndexedDB] Creating formState object store');
            db.createObjectStore('formState');
          }

          // Pending orders store
          if (!db.objectStoreNames.contains(PENDING_ORDERS_STORE)) {
            console.log('➕ [IndexedDB] Creating pendingOrders object store');
            db.createObjectStore(PENDING_ORDERS_STORE);
          }

          // Completed orders store (added in version 2)
          if (!db.objectStoreNames.contains(COMPLETED_ORDERS_STORE)) {
            console.log('➕ [IndexedDB] Creating completedOrders object store');
            db.createObjectStore(COMPLETED_ORDERS_STORE);
          }
        },
        blocked() {
          console.error('🚫 [IndexedDB] Database initialization blocked - another tab may be open');
        },
        blocking() {
          console.warn('⚠️ [IndexedDB] This connection is blocking other connections');
        },
      });

      console.log('✅ [IndexedDB] Database initialized successfully');
    } catch (error) {
      console.error('❌ [IndexedDB] Failed to initialize database:', error);
      this.db = null;
      throw new Error(`Failed to initialize IndexedDB: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Validate database connection and object stores
   */
  private async validateConnection(): Promise<boolean> {
    try {
      if (!this.db) {
        return false;
      }

      // Test the connection by trying to access a basic property
      // This is more reliable than checking constructor names
      const name = this.db.name;
      const version = this.db.version;

      if (!name || typeof version !== 'number') {
        console.warn('⚠️ [IndexedDB] Database connection appears to be invalid');
        this.db = null;
        return false;
      }

      // Additional check: verify the database is not closed
      try {
        // Try to access objectStoreNames - this will throw if DB is closed
        const storeNames = this.db.objectStoreNames;
        if (!storeNames) {
          console.warn('⚠️ [IndexedDB] Database object stores not accessible');
          this.db = null;
          return false;
        }
      } catch (dbError) {
        console.warn('⚠️ [IndexedDB] Database appears to be closed:', dbError);
        this.db = null;
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [IndexedDB] Error validating connection:', error);
      this.db = null;
      return false;
    }
  }

  /**
   * Save form state to IndexedDB with retry logic
   */
  async saveFormState(state: FormState): Promise<void> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      console.log('💾 [IndexedDB] Saving form state...');
      await this.db.put('formState', state, FORM_STATE_KEY);
      console.log('✅ [IndexedDB] Form state saved successfully');
    } catch (error) {
      console.error('❌ [IndexedDB] Error saving form state:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      throw new Error(`Failed to save form state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load form state from IndexedDB with error handling
   */
  async loadFormState(): Promise<FormState | null> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        console.warn('⚠️ [IndexedDB] Connection not available during load, returning null');
        return null;
      }

      if (!this.db) {
        console.warn('⚠️ [IndexedDB] Database not initialized during load');
        return null;
      }

      console.log('📖 [IndexedDB] Loading form state...');
      const state = await this.db.get('formState', FORM_STATE_KEY);
      console.log('✅ [IndexedDB] Form state loaded:', state ? 'Found' : 'Not found');
      return state || null;
    } catch (error) {
      console.error('❌ [IndexedDB] Error loading form state:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      return null; // Return null instead of throwing to allow app to continue
    }
  }

  /**
   * Clear form state from IndexedDB with error handling
   */
  async clearFormState(): Promise<void> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      console.log('🗑️ [IndexedDB] Clearing form state...');
      await this.db.delete('formState', FORM_STATE_KEY);
      console.log('✅ [IndexedDB] Form state cleared successfully');
    } catch (error) {
      console.error('❌ [IndexedDB] Error clearing form state:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      throw new Error(`Failed to clear form state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Save a pending order (for offline submission) with error handling
   */
  async savePendingOrder(order: OrderPayload): Promise<string> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      const orderId = `order-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log('📦 [IndexedDB] Saving pending order:', { orderId, customerEmail: order.customerInfo.email, totalAmount: order.totalAmount });
      await this.db.put(PENDING_ORDERS_STORE, order, orderId);
      console.log('✅ [IndexedDB] Pending order saved successfully:', orderId);
      return orderId;
    } catch (error) {
      console.error('❌ [IndexedDB] Error saving pending order:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      throw new Error(`Failed to save pending order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all pending orders with error handling
   */
  async getPendingOrders(): Promise<Array<{ key: string; value: OrderPayload }>> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        console.warn('⚠️ [IndexedDB] Connection not available during getPendingOrders');
        return [];
      }

      if (!this.db) {
        console.warn('⚠️ [IndexedDB] Database not initialized during getPendingOrders');
        return [];
      }

      console.log('📋 [IndexedDB] Fetching all pending orders...');
      const keys = await this.db.getAllKeys(PENDING_ORDERS_STORE);
      const values = await this.db.getAll(PENDING_ORDERS_STORE);
      const result = keys.map((key, index) => ({ key: key as string, value: values[index] }));
      console.log('📋 [IndexedDB] Found pending orders:', result.length, 'orders');
      if (result.length > 0) {
        console.log('📋 [IndexedDB] Pending orders details:', result.map(o => ({ key: o.key, email: o.value.customerInfo.email, total: o.value.totalAmount })));
      }
      return result;
    } catch (error) {
      console.error('❌ [IndexedDB] Error getting pending orders:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      return []; // Return empty array instead of throwing to allow app to continue
    }
  }

  /**
   * Remove a pending order after successful sync with error handling
   */
  async removePendingOrder(orderId: string): Promise<void> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      console.log('🗑️ [IndexedDB] Removing synced order:', orderId);
      await this.db.delete(PENDING_ORDERS_STORE, orderId);
      console.log('✅ [IndexedDB] Order removed successfully:', orderId);
    } catch (error) {
      console.error('❌ [IndexedDB] Error removing pending order:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      throw new Error(`Failed to remove pending order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clear all pending orders with error handling
   */
  async clearPendingOrders(): Promise<void> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      console.log('🗑️ [IndexedDB] Clearing all pending orders...');
      await this.db.clear(PENDING_ORDERS_STORE);
      console.log('✅ [IndexedDB] All pending orders cleared');
    } catch (error) {
      console.error('❌ [IndexedDB] Error clearing pending orders:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      throw new Error(`Failed to clear pending orders: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if there are any pending orders with error handling
   */
  async hasPendingOrders(): Promise<boolean> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        console.warn('⚠️ [IndexedDB] Connection not available during hasPendingOrders');
        return false;
      }

      if (!this.db) {
        console.warn('⚠️ [IndexedDB] Database not initialized during hasPendingOrders');
        return false;
      }

      const count = await this.db.count(PENDING_ORDERS_STORE);
      console.log('🔍 [IndexedDB] Pending orders count:', count);
      return count > 0;
    } catch (error) {
      console.error('❌ [IndexedDB] Error checking pending orders:', error);
      // Reset connection to force re-initialization on next attempt
      this.db = null;
      return false; // Return false instead of throwing to allow app to continue
    }
  }

  /**
   * Health check for database connection and data integrity
   */
  async healthCheck(): Promise<{ healthy: boolean; message: string; details?: any }> {
    try {
      console.log('🏥 [IndexedDB] Performing health check...');

      // Check if database is initialized
      if (!this.db) {
        return {
          healthy: false,
          message: 'Database not initialized',
          details: { initialized: false }
        };
      }

      // Check connection validity
      const isValid = await this.validateConnection();
      if (!isValid) {
        return {
          healthy: false,
          message: 'Database connection invalid',
          details: { connectionValid: false }
        };
      }

      // Check if object stores exist
      const storeNames = this.db.objectStoreNames;
      const hasFormState = storeNames.contains('formState');
      const hasPendingOrders = storeNames.contains(PENDING_ORDERS_STORE);

      if (!hasFormState || !hasPendingOrders) {
        return {
          healthy: false,
          message: 'Missing required object stores',
          details: {
            hasFormState,
            hasPendingOrders
          }
        };
      }

      // Check pending orders count
      const pendingCount = await this.db.count(PENDING_ORDERS_STORE);

      console.log('✅ [IndexedDB] Health check passed');
      return {
        healthy: true,
        message: 'Database is healthy',
        details: {
          initialized: true,
          connectionValid: true,
          hasFormState,
          hasPendingOrders,
          pendingOrdersCount: pendingCount
        }
      };
    } catch (error) {
      console.error('❌ [IndexedDB] Health check failed:', error);
      return {
        healthy: false,
        message: `Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        details: { error }
      };
    }
  }

  /**
   * Close database connection and cleanup resources
   */
  async close(): Promise<void> {
    try {
      if (this.db) {
        console.log('🔌 [IndexedDB] Closing database connection...');
        await this.db.close();
        this.db = null;
        console.log('✅ [IndexedDB] Database connection closed');
      }
    } catch (error) {
      console.error('❌ [IndexedDB] Error closing database:', error);
    }
  }

  /**
   * Destroy and recreate the database (use with caution)
   */
  async destroy(): Promise<void> {
    try {
      console.log('💥 [IndexedDB] Destroying database...');

      // Close connection first
      await this.close();

      // Delete the database
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => {
          console.log('✅ [IndexedDB] Database destroyed successfully');
          resolve();
        };
        request.onerror = () => {
          console.error('❌ [IndexedDB] Error destroying database');
          reject(new Error('Failed to destroy database'));
        };
        request.onblocked = () => {
          console.warn('⚠️ [IndexedDB] Database deletion blocked - other connections may be open');
        };
      });
    } catch (error) {
      console.error('❌ [IndexedDB] Error destroying database:', error);
      throw new Error(`Failed to destroy database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Save a completed order (for past orders feature)
   */
  async saveCompletedOrder(order: OrderPayload, orderId?: string, synced: boolean = true): Promise<string> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      const savedAt = new Date().toISOString();
      const expiresAt = new Date(Date.now() + COMPLETED_ORDER_TTL).toISOString();
      const key = `completed-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const completedOrder = {
        order,
        savedAt,
        expiresAt,
        orderId,
        synced,
      };

      console.log('📦 [IndexedDB] Saving completed order:', { key, customerEmail: order.customerInfo.email, totalAmount: order.totalAmount });
      await this.db.put(COMPLETED_ORDERS_STORE, completedOrder, key);

      // Enforce max limit - remove oldest if exceeded
      const allOrders = await this.db.getAllKeys(COMPLETED_ORDERS_STORE);
      if (allOrders.length > MAX_COMPLETED_ORDERS) {
        const sortedKeys = allOrders.sort();
        const toRemove = sortedKeys.slice(0, allOrders.length - MAX_COMPLETED_ORDERS);
        for (const removeKey of toRemove) {
          await this.db.delete(COMPLETED_ORDERS_STORE, removeKey);
        }
        console.log(`🧹 [IndexedDB] Removed ${toRemove.length} old completed orders to maintain limit`);
      }

      console.log('✅ [IndexedDB] Completed order saved successfully:', key);
      return key;
    } catch (error) {
      console.error('❌ [IndexedDB] Error saving completed order:', error);
      this.db = null;
      throw new Error(`Failed to save completed order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all non-expired completed orders
   */
  async getCompletedOrders(): Promise<Array<{ key: string; value: { order: OrderPayload; savedAt: string; expiresAt: string; orderId?: string } }>> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        console.warn('⚠️ [IndexedDB] Connection not available during getCompletedOrders');
        return [];
      }

      if (!this.db) {
        console.warn('⚠️ [IndexedDB] Database not initialized during getCompletedOrders');
        return [];
      }

      console.log('📋 [IndexedDB] Fetching completed orders...');
      const keys = await this.db.getAllKeys(COMPLETED_ORDERS_STORE);
      const values = await this.db.getAll(COMPLETED_ORDERS_STORE);

      const now = new Date().toISOString();
      const result: Array<{ key: string; value: { order: OrderPayload; savedAt: string; expiresAt: string; orderId?: string } }> = [];

      for (let i = 0; i < keys.length; i++) {
        const value = values[i];
        // Filter out expired orders
        if (value && value.expiresAt > now) {
          result.push({ key: keys[i] as string, value });
        }
      }

      // Sort by savedAt descending (newest first)
      result.sort((a, b) => new Date(b.value.savedAt).getTime() - new Date(a.value.savedAt).getTime());

      console.log('📋 [IndexedDB] Found completed orders:', result.length, 'orders');
      return result;
    } catch (error) {
      console.error('❌ [IndexedDB] Error getting completed orders:', error);
      this.db = null;
      return [];
    }
  }

  /**
   * Delete a specific completed order by key
   */
  async deleteCompletedOrder(key: string): Promise<void> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        throw new Error('Database connection not available');
      }

      if (!this.db) {
        throw new Error('Database not initialized');
      }

      console.log('🗑️ [IndexedDB] Deleting completed order:', key);
      await this.db.delete(COMPLETED_ORDERS_STORE, key);
      console.log('✅ [IndexedDB] Completed order deleted successfully:', key);
    } catch (error) {
      console.error('❌ [IndexedDB] Error deleting completed order:', error);
      this.db = null;
      throw new Error(`Failed to delete completed order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clean up expired completed orders
   */
  async cleanupExpiredOrders(): Promise<number> {
    try {
      await this.init();

      // Validate connection before proceeding
      if (!(await this.validateConnection())) {
        console.warn('⚠️ [IndexedDB] Connection not available during cleanupExpiredOrders');
        return 0;
      }

      if (!this.db) {
        console.warn('⚠️ [IndexedDB] Database not initialized during cleanupExpiredOrders');
        return 0;
      }

      console.log('🧹 [IndexedDB] Cleaning up expired completed orders...');
      const keys = await this.db.getAllKeys(COMPLETED_ORDERS_STORE);
      const values = await this.db.getAll(COMPLETED_ORDERS_STORE);

      const now = new Date().toISOString();
      let removedCount = 0;

      for (let i = 0; i < keys.length; i++) {
        const value = values[i];
        if (value && value.expiresAt < now) {
          await this.db.delete(COMPLETED_ORDERS_STORE, keys[i]);
          removedCount++;
        }
      }

      if (removedCount > 0) {
        console.log(`✅ [IndexedDB] Cleaned up ${removedCount} expired completed orders`);
      } else {
        console.log('✅ [IndexedDB] No expired completed orders to clean up');
      }

      return removedCount;
    } catch (error) {
      console.error('❌ [IndexedDB] Error cleaning up expired orders:', error);
      this.db = null;
      return 0;
    }
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorage();